/*
Copyright (C) 2020  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import * as React from "react";
import styled from "styled-components";
import {
  Activity,
  Bug,
  ChevronLeft,
  ChevronRight,
  CircleX,
  Info,
  ListChecks,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import {
  MinionPoolDetails,
  MinionPoolEventProgressUpdate,
} from "@src/@types/MinionPool";
import { SectionCard } from "@src/components/ui/DetailsKit";
import EmptyState from "@src/components/ui/Lists/EmptyState";
import {
  Pill,
  T9,
  formatDateTime,
  timeAgo,
} from "@src/components/ui/Lists/ListTable";
import type { PillTone } from "@src/components/ui/Lists/ListTable/ListTable";
import { FilterSelect } from "@src/components/ui/Lists/ListToolbar";
import configLoader from "@src/utils/Config";
import { swiftBrand } from "@src/utils/LabelDictionary";

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;
const Table = styled.div`
  border-top: 1px solid ${T9.slate100};
`;
const grid = `
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) 100px 170px;
  column-gap: 12px;
  padding: 0 20px;
`;
const HeaderRow = styled.div`
  ${grid}
  align-items: center;
  height: 40px;
  background: ${T9.slate50};
  border-bottom: 1px solid ${T9.slate200};
  font-size: 11px;
  line-height: 16px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${T9.slate500};
`;
const Row = styled.div`
  ${grid}
  align-items: start;
  padding-top: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${T9.slate100};
  &:last-child {
    border-bottom: 0;
  }
  &:hover {
    background: ${T9.slate50};
  }
`;
const IconCell = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  height: 20px;
  color: ${props => props.color};
`;
const Message = styled.pre`
  font-family: inherit;
  white-space: pre-line;
  overflow-wrap: anywhere;
  margin: 0;
  font-size: 13px;
  line-height: 20px;
  color: ${T9.slate800};
`;
const Time = styled.div`
  font-size: 13px;
  line-height: 20px;
  color: ${T9.slate500};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;
const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 20px;
  border-top: 1px solid ${T9.slate200};
  font-size: 13px;
  color: ${T9.slate500};
`;
const Pager = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const PageButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid ${T9.slate300};
  background: var(--t9-bg-surface);
  color: ${T9.slate700};
  cursor: pointer;
  padding: 0;
  &:hover:not(:disabled) {
    background: ${T9.slate50};
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;
const Hint = styled.span`
  font-size: 12px;
  color: ${T9.slate500};
`;

type FilterType = "all" | "events" | "progress";
type EventLevel = "DEBUG" | "INFO" | "ERROR";
type OrderDir = "asc" | "desc";
type Props = {
  item?: MinionPoolDetails | null;
};

type State = {
  allEvents: MinionPoolEventProgressUpdate[];
  prevLenghts: number[];
  currentPage: number;
  filterBy: FilterType;
  eventLevel: EventLevel;
  orderDir: OrderDir;
};

const eventKind = (
  event: any,
): { icon: LucideIcon; color: string; label: string; tone: PillTone } => {
  if (event.current_step != null) {
    return { icon: Activity, color: T9.brand500, label: "Progress", tone: "brand" };
  }
  if (event.level === "ERROR") {
    return { icon: CircleX, color: "var(--t9-danger-700)", label: "Error", tone: "danger" };
  }
  if (event.level === "DEBUG") {
    return { icon: Bug, color: T9.slate400, label: "Debug", tone: "neutral" };
  }
  return { icon: Info, color: "var(--t9-info-700)", label: "Info", tone: "info" };
};

class MinionPoolEvents extends React.Component<Props, State> {
  state = {
    allEvents: [] as MinionPoolEventProgressUpdate[],
    prevLenghts: [0, 0],
    currentPage: 1,
    filterBy: "events" as FilterType,
    eventLevel: "INFO" as EventLevel,
    orderDir: "desc" as OrderDir,
  };

  get filteredEventsWithoutPagination() {
    const shouldFilterByEventType = (event: any): boolean => {
      if (this.state.filterBy === "events") {
        return event.level;
      }
      if (this.state.filterBy === "progress") {
        return event.current_step != null;
      }
      return true;
    };
    const shouldFilterByLevel = (event: any): boolean => {
      if (!event.level) {
        return true;
      }
      if (this.state.eventLevel === "INFO") {
        return event.level === "INFO" || event.level === "ERROR";
      }
      if (this.state.eventLevel === "ERROR") {
        return event.level === "ERROR";
      }
      return true;
    };

    return this.state.allEvents
      .filter(
        (event: any) =>
          shouldFilterByEventType(event) && shouldFilterByLevel(event),
      )
      .sort((a: any, b: any) => {
        if (a.index && b.index && this.state.filterBy !== "all") {
          return this.state.orderDir === "asc"
            ? a.index - b.index
            : b.index - a.index;
        }
        const aTime = new Date(a.created_at).getTime();
        const bTime = new Date(b.created_at).getTime();
        return this.state.orderDir === "asc" ? aTime - bTime : bTime - aTime;
      });
  }

  get perPage() {
    return configLoader.config.maxMinionPoolEventsPerPage;
  }

  get filteredEvents() {
    return this.filteredEventsWithoutPagination.filter((_, i) => {
      const minI = this.perPage * (this.state.currentPage - 1);
      const maxI = minI + this.perPage;
      return i >= minI && i < maxI;
    });
  }

  static getDerivedStateFromProps(
    props: Props,
    state: State,
  ): Partial<State> | null {
    if (!props.item) {
      return null;
    }
    const events = props.item?.events || [];
    const progressUpdates = props.item?.progress_updates || [];
    if (
      events.length === state.prevLenghts[0] &&
      progressUpdates.length === state.prevLenghts[1]
    ) {
      return null;
    }

    return {
      allEvents: events.concat(progressUpdates as any),
      prevLenghts: [events.length, progressUpdates.length],
    };
  }

  setOrderDir(orderDir: OrderDir) {
    this.setState({ orderDir, currentPage: 1 });
  }

  filterByType(filterBy: FilterType) {
    this.setState({ filterBy, currentPage: 1 });
  }

  filterByLevel(eventLevel: EventLevel) {
    this.setState({ eventLevel, currentPage: 1 });
  }

  handlePreviousPageClick() {
    this.setState(state => ({ currentPage: state.currentPage - 1 }));
  }

  handleNextPageClick() {
    this.setState(state => ({ currentPage: state.currentPage + 1 }));
  }

  renderBody() {
    return (
      <Table>
        <HeaderRow>
          <div />
          <div>Event / progress update</div>
          <div>Type</div>
          <div>Time</div>
        </HeaderRow>
        {this.filteredEvents.map((event: any) => {
          const kind = eventKind(event);
          const Icon = kind.icon;
          return (
            <Row key={event.id} data-test-id="minionPoolEvents-row">
              <IconCell color={kind.color} title={event.current_step != null ? "Progress update" : "Event"}>
                <Icon size={16} />
              </IconCell>
              <Message data-test-id="minionPoolEvents-message">{swiftBrand(event.message)}</Message>
              <div>
                <Pill tone={kind.tone}>{kind.label}</Pill>
              </div>
              <Time title={formatDateTime(event.created_at)}>
                {formatDateTime(event.created_at)}
                <div style={{ fontSize: 12, lineHeight: "16px", color: T9.slate400 }}>
                  {timeAgo(event.created_at)}
                </div>
              </Time>
            </Row>
          );
        })}
      </Table>
    );
  }

  renderPagination() {
    const total = this.filteredEventsWithoutPagination.length;
    if (total <= this.perPage) {
      return null;
    }
    const totalPages = Math.ceil(total / this.perPage);
    const first = this.perPage * (this.state.currentPage - 1) + 1;
    const last = Math.min(total, first + this.perPage - 1);
    return (
      <Footer data-test-id="minionPoolEvents-pagination">
        <span>
          Showing {first}–{last} of {total}
        </span>
        <Pager>
          <PageButton
            type="button"
            aria-label="Previous page"
            data-test-id="minionPoolEvents-previous"
            disabled={this.state.currentPage === 1}
            onClick={() => {
              this.handlePreviousPageClick();
            }}
          >
            <ChevronLeft size={16} />
          </PageButton>
          <span data-test-id="minionPoolEvents-page">
            {this.state.currentPage} of {totalPages}
          </span>
          <PageButton
            type="button"
            aria-label="Next page"
            data-test-id="minionPoolEvents-next"
            disabled={this.state.currentPage === totalPages}
            onClick={() => {
              this.handleNextPageClick();
            }}
          >
            <ChevronRight size={16} />
          </PageButton>
        </Pager>
      </Footer>
    );
  }

  renderFilters() {
    return (
      <Toolbar>
        <FilterSelect
          label="Show"
          testId="minionPoolEvents-type"
          value={this.state.filterBy}
          defaultValue="events"
          options={[
            { label: "Events", value: "events" },
            { label: "Progress updates", value: "progress" },
            { label: "Events and progress updates", value: "all" },
          ]}
          onChange={value => {
            this.filterByType(value as FilterType);
          }}
        />
        {this.state.filterBy !== "progress" ? (
          <FilterSelect
            label="Level"
            testId="minionPoolEvents-level"
            value={this.state.eventLevel}
            defaultValue="INFO"
            options={[
              { label: "Debug and above", value: "DEBUG" },
              { label: "Info and above", value: "INFO" },
              { label: "Errors only", value: "ERROR" },
            ]}
            onChange={value => {
              this.filterByLevel(value as EventLevel);
            }}
          />
        ) : null}
        <FilterSelect
          label="Order"
          testId="minionPoolEvents-order"
          value={this.state.orderDir}
          defaultValue="desc"
          options={[
            { label: "Newest first", value: "desc" },
            { label: "Oldest first", value: "asc" },
          ]}
          onChange={value => {
            this.setOrderDir(value as OrderDir);
          }}
        />
      </Toolbar>
    );
  }

  render() {
    const isNoData = this.state.allEvents.length === 0;
    const isNoDataFound = this.filteredEvents.length === 0;
    const total = this.filteredEventsWithoutPagination.length;

    if (isNoData) {
      return (
        <SectionCard title="Events" icon={ListChecks} testId="minionPoolEvents">
          <EmptyState
            compact
            icon={ListChecks}
            tone="neutral"
            title="No events yet"
            message="There are no events or progress updates for this SwiftIQ Agent."
          />
        </SectionCard>
      );
    }

    return (
      <SectionCard
        title="Events"
        icon={ListChecks}
        subtitle={
          <>
            {total} {total === 1 ? "entry" : "entries"}
            {this.state.filterBy !== "progress" ? (
              <Hint> · the level applies to events only, not to progress updates</Hint>
            ) : null}
          </>
        }
        actions={this.renderFilters()}
        flush
        testId="minionPoolEvents"
      >
        {!isNoDataFound ? (
          <>
            {this.renderBody()}
            {this.renderPagination()}
          </>
        ) : (
          <EmptyState
            compact
            icon={ListChecks}
            tone="neutral"
            title="No events found"
            message="Nothing matches these filters. Try a lower level or show the progress updates too."
          />
        )}
      </SectionCard>
    );
  }
}

export default MinionPoolEvents;
